import { IDataStore } from '@/services/dataStore/IDataStore'
import { retryTimeouts } from '@/services/dataStore/retryTimeouts'
import useDataStore from '@/services/dataStore/useDataStore'
import { useReferencesStore } from '@/store/references'
import {
  BaseDataLayerReferenceSchema,
  BaseDataLayerSchema,
  IBaseDataLayer,
  IBaseDataLayerReference
} from '@lifk/types'
import useSWRV from 'swrv'
import { computed, ref, watch } from 'vue'

interface GetDataLayerSWRVKey {
  name: string
  dataLayerKey?: string
}
const currentRouteSWRVKey = ref<GetDataLayerSWRVKey>({
  name: 'dataLayer'
})

const useGetDataLayer = () => {
  const {
    dataStoreSWRVKey,
    dataStore,
    handleDataStoreError,
    disableValidations
  } = useDataStore()
  const referencesStore = useReferencesStore()

  const dataLayerKey = computed(() =>
    referencesStore.selectedContentType === 'dataLayer'
      ? referencesStore.queryValues?.contentKey
      : undefined
  )
  currentRouteSWRVKey.value.dataLayerKey = dataLayerKey.value

  async function getDataLayer(
    dataStore: IDataStore,
    dataLayerKey: string,
    isReferenceArticle: boolean
  ): Promise<IBaseDataLayer | IBaseDataLayerReference | undefined> {
    try {
      const response = await retryTimeouts(() =>
        dataStore.getDataLayer(dataLayerKey, isReferenceArticle)
      )

      if (disableValidations.value) return response

      if (isReferenceArticle)
        return BaseDataLayerReferenceSchema.parse(response)
      else return BaseDataLayerSchema.parse(response)
    } catch (e) {
      handleDataStoreError(e, 'dataLayer')
      throw e
    }
  }

  const fetchDataLayerByKey = async (key: string) =>
    (await getDataLayer(dataStore.value, key, false)) as
      | IBaseDataLayer
      | undefined

  // dynamically returns the data layer based on the URL query params
  const getGuidebookDataLayer = () => {
    const result = useSWRV(
      () =>
        `/${currentRouteSWRVKey.value.name}/${dataStoreSWRVKey.value.perspective}/${currentRouteSWRVKey.value.dataLayerKey}`,
      async () => {
        if (currentRouteSWRVKey.value.dataLayerKey) {
          return (await getDataLayer(
            dataStore.value,
            currentRouteSWRVKey.value.dataLayerKey,
            true
          )) as IBaseDataLayerReference | undefined
        } else {
          return undefined
        }
      },
      {
        // 30s for adapters to prevent too many API calls to paid CMS's
        dedupingInterval: 30000,
        shouldRetryOnError: false
      }
    )
    return result
  }

  watch(dataLayerKey, () => {
    currentRouteSWRVKey.value.dataLayerKey = dataLayerKey.value
  })

  return { getGuidebookDataLayer, fetchDataLayerByKey }
}

export { useGetDataLayer }
